import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Moves a one-time cart line onto a selling plan. The cart sections re-render through the same
 * lines-update event Horizon's own quantity changes use, so the drawer, the cart page and the bubble stay in step.
 */
class BcPlanSwitch extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#switch);
  }

  #switch = async ({ currentTarget: button }) => {
    button.disabled = true;
    const { key, plan } = this.dataset;
    const quantity = Number(this.dataset.quantity);
    const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [{ id: key, quantity }], promise: deferred.promise })
    );

    try {
      const body = JSON.stringify({
        id: key,
        quantity,
        selling_plan: Number(plan),
        sections: sections.join(','),
        sections_url: window.location.pathname,
      });
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const cart = await response.json();
      if (!response.ok || cart.errors) throw new Error(cart.description || cart.errors || 'The plan could not be changed.');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'bc-plan-switch', didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      button.disabled = false;
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    }
  };
}

if (!customElements.get('bc-plan-switch')) customElements.define('bc-plan-switch', BcPlanSwitch);

/**
 * One-tap add from the cart offer (bc-cart-offer.liquid). Without JS the form posts to /cart/add.
 */
class BcCartAdd extends HTMLElement {
  connectedCallback() {
    this.querySelector('form')?.addEventListener('submit', this.#add);
  }

  #add = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const button = form.querySelector('button');
    button.disabled = true;
    const data = new FormData(form);
    const item = { id: Number(data.get('id')), quantity: 1 };
    if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));
    const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(item.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items: [item], sections: sections.join(',') }) }));
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || 'Could not add that.');
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: added.sections, items: cart.items, itemCount: cart.item_count, source: 'bc-cart-add', didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      button.disabled = false;
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    }
  };
}

if (!customElements.get('bc-cart-add')) customElements.define('bc-cart-add', BcCartAdd);
